import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Maximize2, MapPin } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { Reveal, SectionHeading } from "./Reveal";
import { MASTERPLAN_IMAGES } from "../data/media";
import { ImageLightbox } from "./ImageLightbox";

export const MasterPlan = () => {
  const { t } = useLang();
  const [active, setActive] = useState(0);
  const [zoom, setZoom] = useState(false);

  return (
    <section id="masterplan" className="relative py-24 md:py-32 bg-[#0A0A0A]" data-testid="masterplan-section">
      <div className="px-6 md:px-12 lg:px-20">
        <SectionHeading overline={t.masterplan.overline} title={t.masterplan.title} text={t.masterplan.text} />

        {/* Tabs */}
        <Reveal delay={0.1}>
          <div className="mt-12 flex flex-wrap gap-2">
            {t.masterplan.tabs.map((tab, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                className={`px-4 py-2.5 text-xs uppercase tracking-[0.14em] border transition-colors duration-300 ${
                  active === i
                    ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/5"
                    : "border-[#2E2E2E] text-[#FFFFF0]/55 hover:border-white/30"
                }`}
                data-testid={`masterplan-tab-${i}`}
              >
                {tab}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-8 grid lg:grid-cols-3 gap-8">
          {/* Image */}
          <Reveal delay={0.15} className="lg:col-span-2">
            <div
              className="group relative aspect-[16/10] overflow-hidden border border-[#2E2E2E] cursor-zoom-in"
              onClick={() => setZoom(true)}
              data-testid="masterplan-image"
            >
              <AnimatePresence mode="wait">
                <motion.img
                  key={active}
                  src={MASTERPLAN_IMAGES[active % MASTERPLAN_IMAGES.length]}
                  alt={t.masterplan.tabs[active]}
                  initial={{ opacity: 0, scale: 1.04 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                  className="w-full h-full object-cover"
                />
              </AnimatePresence>
              <div className="absolute inset-0 bg-black/20 group-hover:bg-black/0 transition-colors" />
              <div className="absolute bottom-4 end-4 flex items-center gap-2 px-3 py-2 bg-black/70 backdrop-blur-sm text-[#FFFFF0] text-xs uppercase tracking-[0.14em]">
                <Maximize2 size={14} strokeWidth={1.5} /> {t.masterplan.zoomHint}
              </div>
            </div>
          </Reveal>

          {/* Legend */}
          <Reveal delay={0.2}>
            <div className="bg-[#141414] border border-[#2E2E2E] p-7 h-full">
              <p className="overline">{t.masterplan.overline}</p>
              <ul className="mt-5 space-y-3">
                {t.masterplan.legend.map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-[#FFFFF0]/70 font-light" data-testid={`masterplan-legend-${i}`}>
                    <MapPin size={14} strokeWidth={1.4} className="text-[#D4AF37] shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </div>

      {zoom && (
        <ImageLightbox
          items={[{ src: MASTERPLAN_IMAGES[active % MASTERPLAN_IMAGES.length], caption: t.masterplan.tabs[active] }]}
          index={0}
          onClose={() => setZoom(false)}
          downloadLabel={t.plans.download}
        />
      )}
    </section>
  );
};
